/*
 * Description:
 * ---------------------
 * AS Path panel. Shows which autonomous systems this appliance's tunnel
 * traverses — per WAN, per cloud anchor, in both directions.
 *
 * The panel is built around one idea: never show an inferred path as if it
 * were a measured one. Every trace carries whether it actually reached the
 * far end, whether its probes shared the tunnel's 5-tuple, and where the
 * path went dark (carrier NAT, silent routers, unannounced space). Those
 * qualifiers are rendered, not hidden, because an operator choosing an
 * anchor on this data needs to know which parts of it are solid.
 *
 * Runs are on-demand and asynchronous: POST /aspath/run starts a sweep,
 * GET /aspath returns state and the last completed report.
 *
 * Data path: reverse-proxy → server → edge /aspath → pkg/aspath
 *
 * ! file ASPathPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useMemo, useState } from "react";
import {
  FiShare2,
  FiPlay,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiEyeOff,
  FiChevronDown,
  FiChevronRight,
  FiGlobe,
  FiArrowRight,
  FiArrowLeft,
  FiHelpCircle,
  FiDatabase,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { fmtDuration } from "../utils/formatters";
import { useWanName, WanLabel } from "./WanNames";
import useVisibleInterval from "../hooks/useVisibleInterval";

// While a sweep is in flight we poll briskly; otherwise the report is static
// and there is nothing to refresh.
const POLL_RUNNING = 3000;
const POLL_IDLE = 20000;

// The tables' columns, also named beside each value where a phone draws a row as a card.
const HOP_COLS = { ttl: "TTL", address: "Address", rtt: "RTT", as: "AS", country: "Country", probe: "Probe" };
const AS_COLS = { as: "AS", network: "Network", country: "Country", hops: "Hops", seen: "Seen on" };

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString();
};

const fmtAge = (secs) => (secs == null ? "—" : fmtDuration(secs, 1));

const fmtMs = (ms) => (ms == null || ms === 0 ? "—" : `${ms.toFixed(1)} ms`);

// Flag emoji from an ISO-3166 alpha-2 code. Country is the whole point of the
// jurisdiction question, so it gets a glyph rather than being buried in text.
const flagOf = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + cc.toUpperCase().charCodeAt(0) - 65,
    base + cc.toUpperCase().charCodeAt(1) - 65
  );
};

// Why a hop has no AS. Kept explicit: "we cannot see this" and "this cannot
// have an AS" are different facts, and only one of them is a data gap.
const KIND_LABEL = {
  public: "public",
  private: "private address",
  cgnat: "carrier NAT",
  linklocal: "link-local",
  loopback: "loopback",
  shared: "benchmark/shared",
  reserved: "reserved",
  multicast: "multicast",
  unmapped: "no announcement found",
  silent: "no reply",
};

/* ═══════════════════════════════════════════════════════════════════════════
   AS CHAIN — the primary visual: the sequence of networks, not of routers
   ═══════════════════════════════════════════════════════════════════════════ */

function ASChain({ segments }) {
  if (!segments || segments.length === 0) {
    return <div className="aspath-chain-empty">No hops answered.</div>;
  }
  return (
    <div className="aspath-chain">
      {segments.map((s, i) => (
        <React.Fragment key={`${s.kind}-${s.first_ttl}-${i}`}>
          {i > 0 && <FiArrowRight className="aspath-chain-arrow" size={13} />}
          {s.kind === "as" ? (
            <div className="aspath-seg aspath-seg--as" title={`TTL ${s.first_ttl}–${s.last_ttl}`}>
              <span className="aspath-seg-asn">AS{s.asn}</span>
              <span className="aspath-seg-name">{s.as_name || "unnamed"}</span>
              {s.country && (
                <span className="aspath-seg-cc">
                  {flagOf(s.country)} {s.country}
                </span>
              )}
              {s.hops > 1 && <span className="aspath-seg-hops">{s.hops} hops</span>}
            </div>
          ) : (
            <div
              className="aspath-seg aspath-seg--opaque"
              title={`TTL ${s.first_ttl}–${s.last_ttl} — ${s.reason}`}
            >
              <FiHelpCircle size={12} />
              <span className="aspath-seg-name">
                {s.hops} {s.hops === 1 ? "hop" : "hops"} · {s.reason}
              </span>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOP TABLE — the raw measurement behind the chain
   ═══════════════════════════════════════════════════════════════════════════ */

function HopTable({ hops }) {
  if (!hops || hops.length === 0) return null;
  return (
    <div className="aspath-hops-wrap">
      <table className="aspath-hops cards-on-phone">
        <thead>
          <tr>{Object.values(HOP_COLS).map((c) => <th key={c}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {hops.map((h) => (
            <tr key={h.ttl} className={h.ip ? "" : "aspath-hop--silent"}>
              <td className="aspath-hop-ttl" data-label={HOP_COLS.ttl}>{h.ttl}</td>
              <td data-label={HOP_COLS.address}>
                {h.ip ? <code>{h.ip}</code> : <span className="aspath-dim">no reply</span>}
                {h.divergent && (
                  <span
                    className="aspath-divergent"
                    title={`The ICMP modality saw ${h.alt_ip} at this hop. Different 5-tuples hashed onto different paths — the address shown is the tunnel's.`}
                  >
                    ⚠ multipath
                  </span>
                )}
              </td>
              <td className="aspath-hop-rtt" data-label={HOP_COLS.rtt}>{fmtMs(h.rtt_ms)}</td>
              <td data-label={HOP_COLS.as}>
                {h.asn ? (
                  <span className="aspath-hop-as">
                    <strong>AS{h.asn}</strong> {h.as_name}
                  </span>
                ) : (
                  <span className="aspath-dim">{KIND_LABEL[h.kind] || h.kind}</span>
                )}
              </td>
              <td data-label={HOP_COLS.country}>
                {h.country ? (
                  <span>
                    {flagOf(h.country)} {h.country}
                  </span>
                ) : (
                  <span className="aspath-dim">—</span>
                )}
              </td>
              <td className="aspath-hop-probe" data-label={HOP_COLS.probe}>{h.probe}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TRACE CARD
   ═══════════════════════════════════════════════════════════════════════════ */

function TraceCard({ trace }) {
  const wanName = useWanName();
  const [open, setOpen] = useState(false);
  const reverse = trace.direction === "reverse";

  return (
    <div className="aspath-trace">
      <button className="aspath-trace-head" onClick={() => setOpen((v) => !v)}>
        {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        <span className={`aspath-dir ${reverse ? "aspath-dir--rev" : ""}`}>
          {reverse ? <FiArrowLeft size={12} /> : <FiArrowRight size={12} />}
          {reverse ? "return" : "outbound"}
        </span>
        <span className="aspath-trace-title">
          <strong>{trace.wan ? <WanLabel iface={trace.wan} /> : `path ${trace.path_id}`}</strong>
          {reverse ? " ← " : " → "}
          {trace.anchor || trace.label}
        </span>

        {trace.complete ? (
          <span className="aspath-badge aspath-badge--ok" title="The far end was positively identified as the last hop.">
            <FiCheckCircle size={11} /> reached
          </span>
        ) : (
          <span
            className="aspath-badge aspath-badge--warn"
            title="No arrival confirmation. The hops shown are real; the path may continue past the last one that answered."
          >
            <FiAlertTriangle size={11} /> unconfirmed
          </span>
        )}

        {!trace.flow_consistent && (
          <span
            className="aspath-badge aspath-badge--warn"
            title="Probes did not carry the tunnel's source port, so on a load-balanced segment these may not be the tunnel's hops."
          >
            off-tuple
          </span>
        )}

        <span className="aspath-trace-meta">
          {trace.hops ? trace.hops.length : 0} hops · {trace.duration_ms} ms
        </span>
      </button>

      <div className="aspath-trace-body">
        <ASChain segments={trace.as_seq} />
        {(trace.notes || []).map((n, i) => (
          <div className="aspath-note" key={i}>
            <FiAlertTriangle size={12} /> {n}
          </div>
        ))}
        {trace.error && (
          <div className="aspath-note aspath-note--err">
            <FiAlertTriangle size={12} /> {trace.error}
          </div>
        )}
        {open && (
          <>
            <div className="aspath-hops-caption">
              {trace.src_ip} :{trace.src_port} → {trace.dst_ip}:{trace.dst_port}
              {trace.iface ? ` · via ${wanName(trace.iface)}` : ""}
            </div>
            <HopTable hops={trace.hops} />
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUMMARY — the distinct networks carrying this appliance's traffic
   ═══════════════════════════════════════════════════════════════════════════ */

function SummaryTable({ summary }) {
  const ases = summary?.ases || [];
  if (ases.length === 0) return null;
  return (
    <div className="aspath-summary">
      <div className="aspath-summary-head">
        <FiGlobe size={14} />
        <span>
          {ases.length} distinct {ases.length === 1 ? "network" : "networks"} observed
          {summary.countries?.length
            ? ` across ${summary.countries.length} ${
                summary.countries.length === 1 ? "country" : "countries"
              }`
            : ""}
        </span>
        {summary.opaque_hops > 0 && (
          <span
            className="aspath-badge aspath-badge--muted"
            title="Hops with no AS attribution: carrier NAT, private addressing, silent routers, or space with no announcement in the dataset. The AS list below is not a complete accounting of the path."
          >
            {summary.opaque_hops} unattributed hops
          </span>
        )}
      </div>
      <div className="aspath-summary-wrap">
        <table className="aspath-summary-table cards-on-phone">
          <thead>
            <tr>{Object.values(AS_COLS).map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {ases.map((a) => (
              <tr key={a.asn}>
                <td>
                  <strong>AS{a.asn}</strong>
                </td>
                <td data-label={AS_COLS.network}>{a.as_name || <span className="aspath-dim">unnamed</span>}</td>
                <td data-label={AS_COLS.country}>
                  {a.country ? (
                    <span>
                      {flagOf(a.country)} {a.country}
                    </span>
                  ) : (
                    <span className="aspath-dim">—</span>
                  )}
                </td>
                <td data-label={AS_COLS.hops}>{a.hops}</td>
                <td className="aspath-summary-seen" data-label={AS_COLS.seen}>
                  {(a.wans || []).join(", ") || "—"}
                  {a.anchors?.length ? ` → ${a.anchors.join(", ")}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PANEL
   ═══════════════════════════════════════════════════════════════════════════ */

export default function ASPathPanel({ nodeApiUrl }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const url = useCallback((p) => (nodeApiUrl ? nodeApiUrl(p) : null), [nodeApiUrl]);

  const load = useCallback(async () => {
    const u = url("/aspath");
    if (!u) return;
    try {
      const r = await fetchWithTimeout(u, {}, 10000);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState(await r.json());
      setError("");
    } catch (e) {
      setError(e.message || "Failed to load AS path state");
    } finally {
      setLoading(false);
    }
  }, [url]);

  const running = !!state?.running;
  useVisibleInterval(load, running ? POLL_RUNNING : POLL_IDLE);

  const startRun = useCallback(async () => {
    const u = url("/aspath/run");
    if (!u) return;
    setBusy(true);
    try {
      const r = await fetchWithTimeout(u, { method: "POST" }, 15000);
      if (!r.ok) {
        const body = await r.text();
        throw new Error(body || `HTTP ${r.status}`);
      }
      setError("");
      // The run is asynchronous; pick up "running" on the next poll.
      await load();
    } catch (e) {
      setError(e.message || "Failed to start the run");
    } finally {
      setBusy(false);
    }
  }, [url, load]);

  const refreshDB = useCallback(async () => {
    const u = url("/aspath/asndb/refresh");
    if (!u) return;
    setBusy(true);
    try {
      const r = await fetchWithTimeout(u, { method: "POST" }, 120000);
      if (!r.ok) {
        const body = await r.text();
        throw new Error(body || `HTTP ${r.status}`);
      }
      setError("");
      await load();
    } catch (e) {
      setError(e.message || "Failed to refresh the ASN dataset");
    } finally {
      setBusy(false);
    }
  }, [url, load]);

  const report = state?.last;
  const db = state?.asn_db;

  const grouped = useMemo(() => {
    const traces = report?.traces || [];
    return {
      forward: traces.filter((t) => t.direction !== "reverse"),
      reverse: traces.filter((t) => t.direction === "reverse"),
    };
  }, [report]);

  if (loading) {
    return <div className="aspath-loading">Loading AS path state…</div>;
  }

  return (
    <div className="aspath-panel">
      {/* ── Control bar ──────────────────────────────────────────────────── */}
      <div className="aspath-bar">
        <button
          className="aspath-run-btn"
          onClick={startRun}
          disabled={busy || running || state?.suppressed}
        >
          {running ? <FiRefreshCw className="aspath-spin" size={14} /> : <FiPlay size={14} />}
          {running ? "Tracing…" : "Run trace"}
        </button>

        <div className="aspath-bar-meta">
          {report ? (
            <>
              Last run {fmtTime(report.run_at)} · {report.duration_ms} ms ·{" "}
              {report.summary?.complete}/{report.summary?.traces} traces reached the far end
            </>
          ) : (
            <>No run yet.</>
          )}
        </div>

        <div className="aspath-db">
          <FiDatabase size={13} />
          {db?.loaded ? (
            <span title={`${db.ranges.toLocaleString()} prefixes from ${db.url}`}>
              ASN data {fmtAge(db.data_age_secs)} old
              {db.stale ? " (stale)" : ""}
            </span>
          ) : (
            <span className="aspath-dim" title={db?.last_error || ""}>
              ASN data not loaded
            </span>
          )}
          <button className="aspath-db-btn" onClick={refreshDB} disabled={busy}>
            <FiRefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {state?.suppressed && (
        <div className="aspath-suppressed">
          <FiEyeOff size={16} />
          <div>
            <strong>Tracing is held back.</strong> {state.suppressed_reason}
          </div>
        </div>
      )}

      {error && (
        <div className="aspath-error">
          <FiAlertTriangle size={14} /> {error}
        </div>
      )}

      {db && !db.loaded && (
        <div className="aspath-note aspath-note--block">
          <FiAlertTriangle size={12} />
          Without the prefix→AS dataset, hops are still measured but arrive unattributed.
          {db.last_error ? ` Last attempt: ${db.last_error}` : ""}
        </div>
      )}

      {/* ── Nothing measured yet ─────────────────────────────────────────── */}
      {!report && !running && (
        <div className="aspath-empty">
          <FiShare2 size={30} opacity={0.15} />
          <h3>No trace has been run</h3>
          <p>
            A run sweeps every WAN toward every configured anchor, then asks the anchor
            currently carrying the tunnel to sweep back. Probes leave from the tunnel's own
            source port so the hops reported are the hops the bonded traffic takes, and they
            are sized to be discarded at the anchor without touching its state.
          </p>
          <p className="aspath-empty-cost">
            Cost: roughly 90 small packets per WAN per anchor per direction, and a visible
            bump on the anchor's bad-magic drop counter. Nothing runs on a schedule.
          </p>
        </div>
      )}

      {/* ── Results ──────────────────────────────────────────────────────── */}
      {report && (
        <>
          <SummaryTable summary={report.summary} />

          {(report.notes || []).map((n, i) => (
            <div className="aspath-note aspath-note--block" key={i}>
              <FiAlertTriangle size={12} /> {n}
            </div>
          ))}

          {grouped.forward.length > 0 && (
            <div className="aspath-group">
              <h4 className="aspath-group-title">Outbound — this appliance to the anchor</h4>
              {grouped.forward.map((t, i) => (
                <TraceCard trace={t} key={`f-${t.wan}-${t.anchor}-${i}`} />
              ))}
            </div>
          )}

          {grouped.reverse.length > 0 && (
            <div className="aspath-group">
              <h4 className="aspath-group-title">
                Return — the anchor back to each WAN
                <span className="aspath-group-sub">
                  Routing is asymmetric, so these are not the outbound paths reversed.
                </span>
              </h4>
              {grouped.reverse.map((t, i) => (
                <TraceCard trace={t} key={`r-${t.wan}-${t.anchor}-${i}`} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
